import { useEffect, useState } from "react";
import { Download, Share, PlusSquare, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/lib/i18n";

const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export function useInstallPrompt() {
  const [prompt, setPrompt] = useState(() => window.__installPrompt || null);
  const [installed, setInstalled] = useState(standalone);
  useEffect(() => {
    const ready = () => setPrompt(window.__installPrompt);
    const done = () => { setInstalled(true); setPrompt(null); window.__installPrompt = null; };
    window.addEventListener("minitai-installable", ready);
    window.addEventListener("appinstalled", done);
    return () => { window.removeEventListener("minitai-installable", ready); window.removeEventListener("appinstalled", done); };
  }, []);
  const ios = isIOS();
  return { canInstall: !installed && (!!prompt || ios), ios: ios && !prompt, prompt };
}

const IOSGuide = ({ open, onOpenChange }) => {
  const { L } = useApp();
  const steps = [
    [Share, L("Tekan butang Kongsi di bar bawah Safari", "Tap the Share button in Safari's bottom bar")],
    [PlusSquare, L("Pilih \"Tambah ke Skrin Utama\" (Add to Home Screen)", "Choose \"Add to Home Screen\"")],
    [Smartphone, L("Tekan \"Tambah\". Buka MinitAI dari ikon di skrin utama.", "Tap \"Add\". Open MinitAI from the home screen icon.")],
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="ios-install-dialog">
        <DialogHeader>
          <DialogTitle>{L("Pasang MinitAI di iPhone", "Install MinitAI on iPhone")}</DialogTitle>
          <DialogDescription>{L("Buka laman ini dalam Safari, kemudian:", "Open this site in Safari, then:")}</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3">
          {steps.map(([Icon, t], i) => (
            <li key={t} className="flex items-center gap-3 rounded-xl bg-slate-50 p-3 text-sm">
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-violet-600 text-xs font-bold text-white">{i + 1}</span>
              <Icon size={18} className="shrink-0 text-violet-600" /> {t}
            </li>
          ))}
        </ol>
      </DialogContent>
    </Dialog>
  );
};

export const InstallAppButton = ({ className = "", testId = "install-app-btn", variant = "outline" }) => {
  const { L } = useApp();
  const { canInstall, ios, prompt } = useInstallPrompt();
  const [guide, setGuide] = useState(false);
  if (!canInstall) return null;
  const install = async () => {
    if (ios) return setGuide(true);
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") window.__installPrompt = null;
  };
  return (
    <>
      <Button type="button" variant={variant} onClick={install} className={`rounded-xl ${className}`} data-testid={testId}>
        <Download size={15} className="mr-1.5" /> {L("Pasang aplikasi", "Install app")}
      </Button>
      <IOSGuide open={guide} onOpenChange={setGuide} />
    </>
  );
};
